import type { ImageLike } from '../../../../utils/assetUrl'
import {
  getDayjsTZDate,
  getDayjsTZDatWithoutTime,
} from '../../../../utils/utils'
import { formatShortGameDateCapitalized } from '../../../../ts-utils'
import { isTimeToBeDefined } from '../../../../ts-utils/date-utils'
import { hasRealVisitingTeam } from '../../placeholderVisitor/placeholderVisitorKeys'
import {
  resolveVisitingTeamDisplay,
  type VisitingTeamDisplaySource,
} from '../../placeholderVisitor/resolveVisitingTeamDisplay'

interface HeaderTeamSource {
  id?: string | null
  name?: string | null
  name_full?: string | null
  image?: ImageLike
}

/**
 * The GET /games/:id record as the Game Attendance header reads it. Raw games
 * carry the zone on `venue`; parsed/feed games on `location` — both accepted.
 */
export type AttendanceGameHeaderSource = Omit<
  VisitingTeamDisplaySource,
  'visitingTeam' | 'visiting_team'
> & {
  starts_at?: string | null
  venue?: { timezone?: string | null } | null
  location?: { timezone?: string | null } | null
  homeTeam?: HeaderTeamSource | null
  home_team?: HeaderTeamSource | null
  visitingTeam?: HeaderTeamSource | null
  visiting_team?: HeaderTeamSource | null
}

export interface AttendanceHeaderTeam {
  name: string | null
  /** Raw image value — resolve with `resolveAssetUrl` at the render site. */
  image: ImageLike
}

export interface AttendanceGameHeader {
  /** e.g. "Mon, Nov 2, 2026 - 2:54 PM EST"; "TBD - Nov 2, 2026" for a TBD time. */
  dateLabel: string | null
  homeTeam: AttendanceHeaderTeam
  visitingTeam: AttendanceHeaderTeam
}

/**
 * Game details for the Game Attendance header (SN-8476): the matchup and the
 * start time in the VENUE's zone (never the viewer's), labelled with its
 * abbreviation. Shared by web + mobile so both show the same text.
 */
export const getAttendanceGameHeader = (
  game: AttendanceGameHeaderSource | null | undefined
): AttendanceGameHeader | null => {
  if (!game) {
    return null
  }

  const startsAt = game.starts_at
  const venueZone = game.venue?.timezone || game.location?.timezone || null
  let dateLabel: string | null = null
  if (startsAt) {
    if (isTimeToBeDefined(startsAt)) {
      const date = getDayjsTZDatWithoutTime(startsAt, venueZone)
      dateLabel = typeof date === 'string' ? `TBD - ${date}` : 'TBD'
    } else {
      dateLabel = formatShortGameDateCapitalized(
        getDayjsTZDate(startsAt, venueZone),
        true
      )
    }
  }

  const home = game.homeTeam || game.home_team
  // A placeholder visitor ("TBD opponent") has a name but no team, so no logo.
  const visitor = hasRealVisitingTeam(game)
    ? game.visitingTeam || game.visiting_team
    : null

  return {
    dateLabel,
    homeTeam: {
      name: home?.name_full ?? home?.name ?? null,
      image: home?.image,
    },
    visitingTeam: {
      name: resolveVisitingTeamDisplay(game).name,
      image: visitor?.image,
    },
  }
}
